import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { IPageState } from '../../contexts/PageContext';
import { CHARGE_TYPES, CURRENCIES, MINTED_SALES, NUMBER_REGEX, PATHS, TRANSACTION_STATUS, TRANSACTION_TYPES, WEIGHT_UNITS } from '../../utils/constant';
import { LINE_ITEM_COLUMNS, ORDER_DETAILS_FORM, getAmount, getMetalType, getTotalValue } from '../$PhysicalOrder';
import { formatDate, getFormattedCurrency, getTwoPointNumber, objToArray } from '../../utils/helpers';
import Billing from '../$Billing';
import Summary from '../$Summary';
import SelectCustomer, { SearchContainer } from '../$SelectCustomer';
import { TransactionContext } from '../../contexts/TransactionContext';
import { AuthContext } from '../../contexts/AuthContext';
import { TTransactionBasic } from '../../Models/Transactions';
import useService from '../../hooks/useService';
import { createAndCompleteMintedPhysicalOrder, createProductScanException, getLotDetails, getPhysicalOrder, getProducts, getTransaction as getTransactionService, updateMintedPhysicalOrder } from '../../services/Transactions';
import { CustomerContext } from '../../contexts/CustomerContext';
import { Button, FormControl, IconButton, InputAdornment, InputLabel, OutlinedInput, TextField, Tooltip } from '@mui/material';
import Modal from '../Modals';
import { AddShoppingCart, Check, RemoveShoppingCart, Search } from '@mui/icons-material';
import { NotificationContext } from '../../contexts/NotificationContext';
import { TMintedSelectedProduct, TProductsColumns, TSku } from '../../Models/PhysicalOrder';
import { TColumn } from '../../Models/common';
import Table from '../custom/Table';
import SerialNumberSelectorModal from '../$PhysicalOrder/SerialNumberSelector';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DynamicForm, { useDynamicForm } from '../forms/DynamicFormNew';
import Title from '../custom/Title';
import Divider from '../custom/Divider';
import AddressCard from '../custom/AddressCard';
import { TCompletePhysicalOrderParams } from '../../services/Transactions/types';
import { getCityStateFromMts, getCustomer, getCustomerActiveAddresses, sendOtpForEmployeeValidation, updateCustomer } from '../../services/Customer';
import { MTSStates, USER_CONFIG, } from '../$SelectCustomer/CustomerDetails';
import { TCustomerFields } from '../../Models/Inbound';
import HighlightsCardAndCustomerDetails from '../custom/HighlightsCardAndCustomerDetails';
import BulkUploadOrderModal from '../Modals/BulkUploadMintedOrders';
import { getPvcCenterAddresses } from '../../services/PVC';
import { ICustomerAddress } from '../../Models/Customer';
import { TPvcCenterAddress } from '../../Models/PVC';
import EmployeeValidationModal from '../Modals/EmployeeValidationModal';

const filteredKeys = [
    'Shift', 'Control', 'Alt', 'Meta', 'Enter', 'ControlLeft', 'ControlRight',
    'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
    'CapsLock', 'Escape', 'Tab', 'Backspace',
    'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12',
    'Insert', 'Delete', 'Home', 'End', 'PageUp', 'PageDown'
];

export type TMintedSalesStpeKeys = 'PHYSICAL_ORDER' | 'BILLING' | 'SUMMARY'

const PAGE_INFO: IPageState = {
    title: 'Minted Sales',
    breadcrumbs: objToArray(MINTED_SALES).map(item => ({ path: item.key, label: item.value })),
    currentPage: MINTED_SALES.PHYSICAL_ORDER,
    path: PATHS.MINTED_SALES
}



type TMintedSalesSummaryKeys =
    'customerId'
    | 'customerName'
    | 'pvcName'
    | 'transactionDate'
    | 'purchasedGold'
    | 'purchasedSilver'
    | 'deliveryCharge'
    | 'totalTaxValue'
    | 'totalCharges'
    | 'roundingRs'
    | 'totalValue'

const SUMMARY_DETAILS: { id: TMintedSalesSummaryKeys, label: any, value: any }[] = [
    {
        id: 'customerId',
        label: "Customer Id",
        value: ""
    },
    {
        id: "customerName",
        label: "Customer Name",
        value: ""
    },
    {
        id: "pvcName",
        label: "PVC Name",
        value: ""
    },
    {
        id: "transactionDate",
        label: "Transaction Date",
        value: ""
    },
    {
        id: "purchasedGold",
        label: "Purchased Gold",
        value: ""
    },
    {
        id: "purchasedSilver",
        label: "Purchased Silver",
        value: ""
    },
    {
        id: 'deliveryCharge',
        label: 'Delivery Charges',
        value: ''
    },
    {
        id: 'totalTaxValue',
        label: "Total Tax Value Rs.",
        value: ''
    },
    {
        id: 'totalCharges',
        label: "Total Charges Rs.",
        value: ''
    },
    {
        id: 'roundingRs',
        label: "Rounding Rs.",
        value: ''
    },
    {
        id: 'totalValue',
        label: "Grand Total Rs.",
        value: ''
    },

]

const Container = styled.div`
    width: 100%;    
`

const MintedSales = () => {

    const { pvcCenterDetails, setTransactionInfo, transactionInfo, resetOrder, setCapturedSteps, physicalOrder } = useContext(TransactionContext)
    const { globalTransactionNumber, status, updatedAt } = transactionInfo
    const { user } = useContext(AuthContext)
    const { customerConfig } = useContext(CustomerContext)
    const [searchParams] = useSearchParams()
    const { id } = useParams()
    const { execute } = useService()
    const navigate = useNavigate()
    const step = searchParams.get('section')
    const [showBulkUploader, setShowBulkUploader] = useState(false)

    const getTransaction = async () => {
        const { data } = await execute(() => getTransactionService({ globalTransactionNumber: id!, transactionType: "MINTED_SALES" }))
        // if (data?.status === TRANSACTION_STATUS.COMPLETED) await execute(() => getLotDetails({ globalTransactionNumber: data?.globalTransactionNumber! }))
        if (data !== null) {
            switch (data.status) {
                case TRANSACTION_STATUS.PHYSICAL_ORDER_INITIATED:
                    navigate(PATHS.MINTED_SALES + '/' + id + '?section=PHYSICAL_ORDER')
                    break;
                case TRANSACTION_STATUS.PAYMENT_INITIATED:
                    setCapturedSteps(['PHYSICAL_ORDER'])
                    navigate(PATHS.MINTED_SALES + '/' + id + '?section=BILLING')
                    break;
                case TRANSACTION_STATUS.COMPLETED:
                case TRANSACTION_STATUS.CANCELLED:
                case TRANSACTION_STATUS.PENDING_CANCELLATION:
                    setCapturedSteps(['PHYSICAL_ORDER', 'BILLING'])
                    navigate(PATHS.MINTED_SALES + '/' + id + '?section=SUMMARY')
                    break;
            }

        }
    }

    const PageContent = useCallback(() => {
        switch (step as TMintedSalesStpeKeys) {
            case 'PHYSICAL_ORDER':
                return <>
                    <CustomerDetail />
                    <MintedPhysicalOrder />
                </>

            case 'BILLING':
                return <>
                    <CustomerDetail />
                    <MintedPhysicalOrder />
                    <Billing path={PATHS.MINTED_SALES} nextSection='SUMMARY' prevSection='PHYSICAL_ORDER' nextButtonLable='Generate Invoice' />
                </>

            case 'SUMMARY':
                return <>
                    <MintedPhysicalOrder />
                    <Billing path={PATHS.MINTED_SALES} nextSection='SUMMARY' prevSection='PHYSICAL_ORDER' nextButtonLable='Generate Invoice' />
                    <Summary prevSection='BILLING' path={PATHS.MINTED_SALES} transitionHistory={[]} summaryDetails={getSummary()} />
                </>

            default:
                return <>
                    <CustomerDetail />
                    <MintedPhysicalOrder />
                </>
        }
    }, [step])

    const getSummary = () => {
        let totalTaxValue = 0
        physicalOrder.taxes?.forEach((tax) => {
            totalTaxValue += tax.taxAmount
        })
        let totalCharges = 0
        physicalOrder.charges?.forEach((charge) => {
            totalCharges += charge.preTaxAmount
            totalTaxValue += charge.postTaxAmount - charge.preTaxAmount
        })
        const roundingRs = getTwoPointNumber((Math.round(physicalOrder.totalPrice || 0) - (physicalOrder.totalPrice || 0)) ?? 0)
        return SUMMARY_DETAILS.map(field => {
            switch (field.id) {
                case 'customerId':
                    field.value = customerConfig.customerRefNo
                    break;
                case 'customerName':
                    field.value = customerConfig.fullName
                    break;
                case 'pvcName':
                    field.value = pvcCenterDetails.name
                    break;
                case 'purchasedGold':
                    field.value = physicalOrder.totalGoldWeight ? physicalOrder.totalGoldWeight : 0 + ' gm'
                    break;
                case 'purchasedSilver':
                    field.value = physicalOrder.totalSilverWeight ? physicalOrder.totalSilverWeight : 0 + ' gm'
                    break;
                case 'transactionDate':
                    field.value = updatedAt ? formatDate(updatedAt) : ''
                    break;
                case 'deliveryCharge':
                    const deliveryCharge = physicalOrder.charges?.filter((charge) => {
                        return charge.chargeId === CHARGE_TYPES.DELIVERY_CHARGE
                    })[0]
                    field.value = deliveryCharge ? getFormattedCurrency(deliveryCharge.postTaxAmount) : null
                    break;
                case 'totalTaxValue':
                    field.value = getFormattedCurrency(getTwoPointNumber(totalTaxValue))
                    break;
                case 'totalCharges':
                    field.value = getFormattedCurrency(getTwoPointNumber(totalCharges))
                    break;
                case 'roundingRs':
                    field.value = getFormattedCurrency(getTwoPointNumber(roundingRs))
                    break;
                case 'totalValue':
                    field.value = getFormattedCurrency(getTwoPointNumber(physicalOrder.totalPrice || 0))
                    break;
            }
            return field
        })
    }

    const cancelTransactionHandler = (transactionId: string) => {
        navigate(PATHS.PVC_TRANSACTIONS)
    }

    useEffect(() => {
        resetOrder()
        if (id) {
            getTransaction()
        }
        else {
            setTransactionInfo({
                currency: CURRENCIES.INR,
                pvcCenterId: user.centerId!,
                status: TRANSACTION_STATUS.PHYSICAL_ORDER_INITIATED,
                transactionType: TRANSACTION_TYPES.MINTED_SALES,
                weightUOM: WEIGHT_UNITS.GM,
            } as TTransactionBasic)
        }
        return () => resetOrder()
    }, [user])

    return (
        <Container>
            {id ? <HighlightsCardAndCustomerDetails
                customerConfig={customerConfig}
                globalTransactionNumber={globalTransactionNumber as string}
                status={status}
                transactionType={transactionInfo.transactionType}
                onSuccessCancelTransaction={cancelTransactionHandler}
            />
                : <>
                    <Button variant='outlined' onClick={() => setShowBulkUploader(true)}>Bulk Upload</Button>
                </>
            }
            <PageContent />
            <BulkUploadOrderModal
                open={Boolean(showBulkUploader)}
                onClose={() => setShowBulkUploader(false)}
            />
        </Container>
    )
}


const MintedPhysicalOrder = () => {
    const [searchString, setSearchString] = useState('')
    const { products, cart, setCart, transactionInfo, lineItems, capturedSteps, physicalOrder, setCapturedSteps, billingData, isEmployeeVerified, setIsEmployeeVerified } = useContext(TransactionContext)
    const { notify } = useContext(NotificationContext)
    const { execute } = useService()
    const { customerConfig } = useContext(CustomerContext)
    const { id } = useParams()
    const navigate = useNavigate()
    const [showSerialNumberModal, setShowSerialNumberModal] = useState<boolean | string>(false);
    const [availableSerialNumbers, setAvailableSerialNumbers] = useState<{ [key: string]: number[] }>({})
    const [selectedSerialNumbers, setSelectedSerialNumbers] = useState<{ [key: string]: number[] }>({})
    const [quantity, setQuantity] = useState<{ [key: string]: string }>({})
    const [forceNavigate, setForceNavigate] = useState(false)
    const [filteredProducts, setFilteredProducts] = useState(products.filter((item) => {
        if (item.inventory.inventory_type === 1 || item.inventory.inventory_type === 7)
            return item
    }))
    const [showProducts, setShowProducts] = useState(products.filter((item) => {
        if (item.inventory.inventory_type === 1 || item.inventory.inventory_type === 7)
            return item
    }))
    // const [selectedProducts, setSelectedProducts] = useState<TProductsColumns[]>([])
    const [selectedProducts, setSelectedProducts] = useState<TMintedSelectedProduct[]>([])
    const qrRef = useRef<HTMLInputElement>(null);
    const [openEmployeeValidationModal, setOpenEmployeeValidationModal] = useState(false);
    const [otpSentTo, setOtpSentTo] = useState('');
    const [formData, setFormData] = useState<any>();

    useEffect(() => {
        getAllProducts()
    }, [])

    useEffect(() => {
        let data: any = products?.filter((item) => {
            if (item.inventory.inventory_type === 1 || item.inventory.inventory_type === 7)
                return item
        })
        if (data.length > 0) {
            setFilteredProducts(data)
            setShowProducts(data)
        }
    }, [products])

    useEffect(() => {
        let selectProduct: any = [];
        let cartItem: any = [];
    
        if (lineItems.length > 0) {
            lineItems.forEach((item: any) => {
                let data = {
                    weight: item.weight!,
                    skuId: item.productId!,
                    name: item.product.product_name!,
                    product_name: item.product.product_name!,
                    inventory: {
                        quantity: item.quantity!, // available qty
                        serial_numbers: item.product.is_serial_no ? '[]' : '',
                    },
                    description: item.product.description!,
                    metalType: item.metalType!,
                    currencyPair: item.metalType!,
                    purity: item.product.metal_purity!,
                    metal_purity: item.product.metal_purity!,
                    isSerialNo: item.product.is_serial_no!,
                    orderQty: item.quantity!, // available qty
                };
    
                let cartData = {
                    productId: item.productId,
                    discount: item.discount,
                    isDigital: item.isDigital,
                    metalType: item.metalType!,
                    quantity: item.quantity,
                    serialNumbers: (() => {
                        try {
                            return item.product?.is_serial_no ? JSON.parse(item?.serialNumbers) : '';
                        } catch (e) {
                            return [];
                        }
                    })(),
                    totalTax: item.totalTax,
                    totalValue: item.totalValue,
                    unitPrice: item.unitPrice,
                    unitPriceTaxInclusive: item.unitPriceTaxInclusive,
                    weight: item.weight,
                    fineWeight: item?.product?.fine_weight,
                    isSerialNo: item.product.is_serial_no,
                    productName: item.product.product_name,
                };
    
                selectProduct.push(data);
                cartItem.push(cartData);
            });
    
            setSelectedProducts(selectProduct);
            setCart(cartItem);
        }
    }, [lineItems]);
    

    

    useEffect(() => {
        searchProducts()
    }, [searchString])

    useEffect(() => {
        if (forceNavigate) goToNextSection()
    }, [forceNavigate])


    useEffect(() => {// for serial number in the selected product
        selectedProducts.map((product) => {
            let parsedSerialNumbers: any = []
            if (product.isSerialNo) {
                parsedSerialNumbers = (() => {
                    try {
                        return JSON.parse(product.inventory.serial_numbers);
                    } catch (e) {
                        return [];
                    }
                })()
                setAvailableSerialNumbers((prev) => ({ ...prev, [product.skuId]: parsedSerialNumbers }))
            }
            lineItems.length > 0 && lineItems.map((lineItem) => {
                if (lineItem?.product?.is_serial_no && product.skuId === lineItem.productId) {
                    const prevSerialNo =  (() => {
                        try {
                            return JSON.parse(lineItem.serialNumbers);
                        } catch (e) {
                            return [];
                        }
                    })()         
                    prevSerialNo?.forEach((el: any) => {
                        if (!parsedSerialNumbers.includes(el))
                            parsedSerialNumbers.push(el)
                    });
                    parsedSerialNumbers.sort((a: any, b: any) => a - b)
                    setAvailableSerialNumbers((prev) => ({ ...prev, [product.skuId]: parsedSerialNumbers }))
                }
            })
        })
    }, [selectedProducts])

    useEffect(() => {
        if (physicalOrder?.id) {
            execute(() => getPhysicalOrder({
                globalTransactionNumber: transactionInfo.globalTransactionNumber!,
                transactionType: transactionInfo.transactionType
            }))
        }
    }, [])

    const getAllProducts = async () => {
        await execute(() => getProducts({}))
    }

    const handleSearch = (e: any) => {
        if (Object.keys(customerConfig).length === 0) notify('Select Customer First', 'error')
        else setSearchString(e.target.value)
    }

    const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if(!filteredKeys.includes(event.code) && !event.shiftKey && !event.altKey && !event.ctrlKey)
            handleqrCodeScan((event.target as HTMLInputElement).value);
    };

    const handleqrCodeScan = (searchString: string) => {
        const serial = searchString.substring(searchString.lastIndexOf('/') + 1);
        if (searchString.includes('scan.mmtcpamp.com') && serial.length === 7 && !isNaN(Number(serial))) {
            const skuId = searchString.substring(searchString.lastIndexOf('?p=')+3, searchString.lastIndexOf('/'));
            const serialNumber = parseInt(searchString.substring(searchString.lastIndexOf('/') + 1));
            const product = filteredProducts.find(elem => elem.skuId === skuId);
            const allSerialNumbers = product ? JSON.parse(product.inventory.serial_numbers) as Array<number> : [];
            setSearchString(skuId);
            qrRef.current!.value = "";
            if(!product) {
                notify('Product not in list.', 'error');
                createProductScanException({ exceptionType: 'PRODUCT_NOT_PRESENT', scannedUrl: searchString.substring(searchString.lastIndexOf('https')) })();
            }
            else if(product.inventory.quantity === 0) {
                notify('Product not in stock.', 'error');
                createProductScanException({ exceptionType: 'PRODUCT_OUT_OF_STOCK', scannedUrl: searchString.substring(searchString.lastIndexOf('https')) })();
            }
            else if(product?.isSerialNo && !allSerialNumbers.includes(serialNumber)) {
                notify('Serial number not in list.', 'error');
                createProductScanException({ exceptionType: 'SERIAL_NUMBER_NOT_PRESENT', scannedUrl: searchString.substring(searchString.lastIndexOf('https')) })();
            }
            else {
                SelectProductByQRCode(product, serialNumber);
            }
        }
    }

    const SelectProductByQRCode = (product: TSku, serialNumber: number) => {
        if (Object.keys(customerConfig).length === 0) {
            notify('Select Customer First', 'error');
            return;
        }

        const isSelected = selectedProducts.find(elem => elem.skuId === product.skuId);
        const previousEntry = cart.find(elem => elem.productId === product.skuId);
        const previousQuantity = quantity[product.skuId];

        if(previousEntry && previousEntry.serialNumbers.split(',').includes(serialNumber.toString())) {
            notify('Product with serial number already added.', 'error');
            return;
        }

        if(!isSelected)
            setSelectedProducts((prev: any) => ([...prev, product]));

        setQuantity(prev => ({ ...prev, [product.skuId]: previousQuantity ? (Number(previousQuantity) + 1).toString() : '1' }));
        setSelectedSerialNumbers(prev => ({ ...prev, [product.skuId]: selectedSerialNumbers[product.skuId] ? [ ...selectedSerialNumbers[product.skuId], serialNumber ] : [ serialNumber ]  }));

        if(previousEntry) { // Update if product in the cart
            setCart(prev => {
                return prev.map(elem => {
                    if(elem.productId === product.skuId) {
                        return {
                            ...elem,
                            quantity: elem.quantity + 1,
                            serialNumbers: elem.serialNumbers + ',' + serialNumber.toString(),
                            totalTax: getAmount(product.taxAmount, elem.quantity + 1, customerConfig),
                            totalValue: getAmount(product.postTaxAmount, elem.quantity + 1, customerConfig),
                            unitPrice: getAmount(product.preTaxAmount, elem.quantity + 1, customerConfig),
                            unitPriceTaxInclusive: getAmount(product.postTaxAmount, elem.quantity + 1, customerConfig)
                        }
                    } else {
                        return elem;
                    }
                })
            })
        } else { // Insert if product not in the cart
            setCart(prev => ([
                ...prev,
                {
                    productId: product.skuId,
                    discount: 0,
                    isDigital: true,
                    metalType: getMetalType(product),
                    quantity: 1,
                    serialNumbers: serialNumber.toString(),
                    taxPercentage: product.taxPercentage,
                    totalTax: getAmount(product.taxAmount, 1, customerConfig),
                    totalValue: getAmount(product.postTaxAmount, 1, customerConfig),
                    unitPrice: getAmount(product.preTaxAmount, 1, customerConfig),
                    unitPriceTaxInclusive: getAmount(product.postTaxAmount, 1, customerConfig),
                    weight: product.weight,
                    fineWeight: product.fineWeight,
                    isSerialNo: product.isSerialNo,
                    productName: product.name
                }]
            ))
        }
    }

    const searchProducts = () => {
        if (searchString.length === 0) return setShowProducts(filteredProducts)
        if (searchString.length > 1) {
            let filteredData = filteredProducts.filter((product: any) => {
                if (
                    (product.inventory?.serial_numbers &&
                        product.inventory.serial_numbers
                            .toString()
                            .toLowerCase()
                            .includes(searchString.toLowerCase())) ||
                    product.name
                        .toLowerCase()
                        .includes(searchString.toLowerCase()) ||
                    product.purity
                        .toString()
                        .includes(searchString.toLowerCase()) ||
                    product.description
                        .toLowerCase()
                        .includes(searchString.toLowerCase()) ||
                    getMetalType(product)
                        .toLowerCase()
                        .includes(searchString.toLowerCase()) ||
                    product.skuId
                        .toLowerCase()
                        .includes(searchString.toLowerCase())
                ) {
                    return product;
                }
            });
            setShowProducts(filteredData)
        }
    }

    const getTwoPointNumber = (number: number) => {
        return Number(number.toFixed(2))
    }

    const DisSelectProduct = (params: TProductsColumns) => {
        setSelectedProducts(prev => ([...prev.filter(selectedProducts => params.skuId != selectedProducts.skuId)]))
        setCart(prev => ([...prev.filter(cartItem => params.skuId != cartItem.productId)]))
    }

    const SelectProduct = (params: TProductsColumns) => {
        if (Object.keys(customerConfig).length === 0) {
            notify('Select Customer First', 'error')
            return
        }
        setSelectedProducts((prev: any) => ([...prev, params]))
    }
    const openSerialModal = (id: string) => {
        setShowSerialNumberModal(id);
    }

    // const initializeStates = () => {
    //     const rows = selectedProducts
    //     setQuantity(
    //         rows
    //             .reduce((productsObj, product) => {
    //                 return { ...productsObj, [product.skuId]: '0' }
    //             }, {})
    //     )
    //     setSelectedSerialNumbers(
    //         rows
    //             .reduce((productsObj, product) => {
    //                 if (product.isSerialNo) {
    //                     return { ...productsObj, [product.skuId]: [] }
    //                 } else {
    //                     return productsObj
    //                 }
    //             }, {})
    //     )
    // }

    const removeFromCart = (id: string) => {
        setQuantity(prev => ({ ...prev, [id]: '0' }))
        setSelectedSerialNumbers(prev => ({ ...prev, [id]: [] }));
        setCart(prev => ([...prev.filter(cartItem => id != cartItem.productId)]))
        setSelectedProducts(prev => ([...prev.filter(item => id != item.skuId)]))

    }
    const addToCart = (id: string) => {
        const product = selectedProducts.filter(product => id === product.skuId)[0]
        setQuantity(prev => ({ ...prev, [id]: '1' }))

        setCart(prev => ([
            ...prev,
            {
                productId: product.skuId,
                discount: 0,
                isDigital: true,
                metalType: getMetalType(product),
                quantity: 1,
                serialNumbers: '',
                taxPercentage: product.taxPercentage,
                totalTax: getAmount(product.taxAmount, 1, customerConfig),
                totalValue: getAmount(product.postTaxAmount, 1, customerConfig),
                unitPrice: getAmount(product.preTaxAmount, 1, customerConfig),
                unitPriceTaxInclusive: getAmount(product.postTaxAmount, 1, customerConfig),
                weight: product.weight,
                fineWeight: product.fineWeight,
                isSerialNo: product.isSerialNo,
                productName: product.name
            }]
        ))
    }

    const quantityHandler = (id: string, quantity: number, product: TProductsColumns) => {
        if (!quantity) {
            removeFromCart(id)
        }
        else {
            const totalTax = getAmount(product.taxAmount, quantity, customerConfig)
            const totalValue = getTotalValue(product, quantity, customerConfig)
            setCart(prev => ([
                ...prev.map(cartItem => {
                    return id === cartItem.productId ? {
                        ...cartItem,
                        quantity,
                        totalTax,
                        totalValue
                    } : cartItem
                })
            ]
            ))
        }
    }

    const changeQuantity = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, id: string) => {
        const value = e.target.value
        const product = selectedProducts.filter(product => id === product.skuId)[0]
        if (!product.taxAmount) {
            let productData = filteredProducts.filter(product => id === product.skuId)[0]
            product.taxAmount = productData.taxAmount
        }
        if (!value.length) return setQuantity(prev => ({ ...prev, [id]: '0' }))
        else if (NUMBER_REGEX.test(value)) {
            let newValue = value.replace(/^0+/, "");
            setSelectedSerialNumbers(prev => ({ ...prev, [id]: [] }))
            setQuantity(prev => ({ ...prev, [id]: newValue }))
        }
        if (product.taxAmount)
            quantityHandler(id, Number(value), product)
    }

    const productColumns: TColumn<TProductsColumns>[] = [
        {
            field: 'skuId',
            headerName: 'Item Number',
            minWidth: 250,
            flex:1
        },
        {
            field: 'name',
            headerName: 'Product Name',
            minWidth: 350,
            flex:1
        },
        // {
        //     field: 'description',
        //     headerName: 'Item Description',
        //     minWidth: 250,
        //     renderCell(params) {
        //         let div = document.createElement("div");
        //         div.innerHTML = params.row.description;
        //         let text = div.textContent || div.innerText || "";
        //         return text
        //     }
        // },
        {
            field: 'currencyPair',
            headerName: 'Metal Type',
            flex:1
        },
        {
            field: 'purity',
            headerName: 'Purity',
            flex:1
        },
        {
            field: 'weight',
            headerName: 'Unit Weight',
            flex:1
        },
        {
            field: 'stock',
            headerName: 'Available Stock',
            minWidth: 130,
            renderCell(params) {
                const { quantity } = params.row.inventory
                return quantity
            },
            flex:1
        },
        {
            field: 'actions',
            headerName: 'Add to Cart',
            renderCell(params) {
                return selectedProducts?.filter(product => params.row.skuId === product.skuId)[0] ?
                    <Tooltip title='Remove From Cart'>
                        <IconButton onClick={() => DisSelectProduct(params.row as unknown as TProductsColumns)}>
                            <RemoveShoppingCart />
                        </IconButton>
                    </Tooltip> :
                    <Tooltip title='Add To Cart'>
                        <IconButton onClick={() => SelectProduct(params.row as unknown as TProductsColumns)}>
                            <AddShoppingCart />
                        </IconButton>
                    </Tooltip>
            },
            flex:1
        },
    ]

    const AddedProductColumns: TColumn<TProductsColumns>[] = [
        {
            field: 'skuId',
            headerName: 'Item Number',
            minWidth: 180,
            flex:1
        },
        {
            field: 'name',
            headerName: 'Product Name',
            minWidth: 250,
            flex:1
        },
        // {
        //     field: 'description',
        //     headerName: 'Item Description',
        //     minWidth: 250,
        //     renderCell(params) {
        //         let div = document.createElement("div");
        //         div.innerHTML = params.row.description;
        //         let text = div.textContent || div.innerText || "";
        //         return text
        //     },
        // },
        {
            field: 'currencyPair',
            headerName: 'Metal Type',
            flex:1
        },
        {
            field: 'purity',
            headerName: 'Purity',
            flex:1
        },
        {
            field: 'weight',
            headerName: 'Unit Weight',
            flex:1
        },
        {
            field: 'totalWeight',
            headerName: 'Net Weight',
            renderCell(params) {
                const itemInCart = cart.filter(product => params.id === product.productId)[0]
                return itemInCart ? getTwoPointNumber(params.row.weight * itemInCart.quantity) : 0
            },
            flex:1
        },
        {
            field: 'amount',
            headerName: 'Amount (Incl. Taxes)',
            minWidth: 130,
            renderCell(params) {
                const itemInCart = cart.filter(product => params.id === product.productId)[0]
                return itemInCart ? getTwoPointNumber(itemInCart.totalValue) : 0
            },
            flex:1
        },
        {
            field: 'orderQty',
            headerName: 'Ordered Qty',
            minWidth: 100,
            flex:1
        },
        {
            field: 'serialNumbers',
            headerName: 'Serial Numbers',
            minWidth: 60,
            renderCell(params) {
                return params.row.isSerialNo ?
                    <>
                        {quantity[params.row.skuId] !== '0' && <IconButton onClick={() => openSerialModal(params.row.skuId)}>
                            <VisibilityIcon />
                        </IconButton>}
                    </>
                    : <></>
            },
            flex:1
        },
        {
            field: 'quantity',
            headerName: 'Quantity',
            minWidth: 150,
            renderCell(params) {
                return <TextField
                    // disabled={!cart?.filter(product => params.id === product.productId)[0]}
                    disabled={Boolean(params.row.orderQty) ? Boolean(params.row.orderQty) : !cart?.filter(product => params.id === product.productId)[0]}
                    value={params.row.orderQty ?? quantity[params.id]}
                    onChange={e => changeQuantity(e, params.id as string)}
                    size='small'
                    onKeyDown={e => e.stopPropagation()}
                />
            },
            flex:1
        },
        {
            field: 'actions',
            headerName: '',
            renderCell(params) {
                return cart?.filter(product => params.id === product.productId)[0] ?
                    <Tooltip title='Remove From Cart'>
                        <IconButton onClick={() => removeFromCart(params.id as string)}>
                            <RemoveShoppingCart />
                        </IconButton>
                    </Tooltip> :
                    <Tooltip title='Add To Cart'>
                        <IconButton onClick={() => addToCart(params.id as string)}>
                            <AddShoppingCart />
                        </IconButton>
                    </Tooltip>
            },
            flex:1
        },
    ]


    const goToNextSection = () => {
        let path = PATHS.MINTED_SALES, nextSection = 'BILLING'
        if (!capturedSteps.includes('PHYSICAL_ORDER'))
            return notify('Complete Physical Order first.', 'error')

        if (id) navigate(path + '/' + id + '?section=' + nextSection)
        else navigate(path + '?section=' + nextSection)
    }
    const submitHandler = async (formData: any) => {
        let totalGoldWeight = 0;
        let totalSilverWeight = 0;
        for(let i = 0; i < cart.length; i++){
            if(cart[i].metalType =="gold")
                totalGoldWeight += cart[i].weight
           
            if(cart[i].metalType =="silver")
                totalSilverWeight += cart[i].weight

        }
        // if(customerConfig.userType == "employee"){
       
        //   let totalFinancialOrderGoldWeight =  transactionInfo?.customerPurchaseHistory?.employee_total_gold_weight + totalGoldWeight
        //     if(totalFinancialOrderGoldWeight > METAL_LIMIT_FOR_FINANCIAL_YEAR.EMPLOYEE_GOLD_LIMIT)
        //         return notify('employee cannot buy more gold than the limit set for the financial year.','error')

          
        //   let totalFinancialOrderSilverWeight =  transactionInfo?.customerPurchaseHistory?.employee_total_silver_weight + totalSilverWeight
        //   if(totalFinancialOrderSilverWeight > METAL_LIMIT_FOR_FINANCIAL_YEAR.EMPLOYEE_SILVER_LIMIT)
        //     return notify('employee cannot buy more silver than the limit set for the financial year.','error')
        // }
        // if(customerConfig.userType == "F&F"){
          
        //   let totalFinancialOrderGoldWeight =  transactionInfo?.customerPurchaseHistory?.friends_and_family_total_gold_weight + totalGoldWeight
        //     if(totalFinancialOrderGoldWeight > METAL_LIMIT_FOR_FINANCIAL_YEAR.FRIENDS_AND_FAMILY_GOLD_LIMIT)
        //         return notify('F&F cannot buy more gold than the limit set for the financial year.','error')

          
        //   let totalFinancialOrderSilverWeight =  transactionInfo?.customerPurchaseHistory?.friends_and_family_total_silver_weight + totalSilverWeight
        //   if(totalFinancialOrderSilverWeight > METAL_LIMIT_FOR_FINANCIAL_YEAR.FRIENDS_AND_FAMILY_SILVER_LIMIT)
        //     return notify('F&F cannot buy more silver than the limit set for the financial year.','error')
        // }

        if(lineItems?.length === 0 && (customerConfig.userType === "F&F" || customerConfig.userType === "employee")) {
            const { data, error } = await execute(() => sendOtpForEmployeeValidation({ dgCustomerRefNo: customerConfig.dgCustomerRefNo! }));
            if(error === null && data?.status === 'ok') {
                setOtpSentTo(data.otpSentTo);
                setFormData(formData);
                setOpenEmployeeValidationModal(true);
            }
        } else {
            finalSubmit(formData);
        }
    }

    const finalSubmit = async (formData: any) => {
        for (let lineItem of cart) {
            if (lineItem.isSerialNo && selectedSerialNumbers[lineItem.productId]) {
                const serialNumbersOfSku = selectedSerialNumbers[lineItem.productId]

                if (lineItem.isSerialNo && serialNumbersOfSku.length > lineItem.quantity) {
                    return notify(`Serial Numbers can't be more than quantity for item ${lineItem.productName}`, 'error')
                } else if (lineItem.isSerialNo && serialNumbersOfSku.length < lineItem.quantity) {
                    return notify(`Serial Numbers can't be less than quantity for item ${lineItem.productName}`, 'error')
                }
            }
        }

        const cartItemsWithoutSerialNumbers = []
        if (!cart.length) {
            return notify('Add Products into cart', 'error')
        }
        for (let cartItem of cart) {
            if (!cartItem.serialNumbers && cartItem.isSerialNo) cartItemsWithoutSerialNumbers.push(cartItem)
        }

        if (cartItemsWithoutSerialNumbers.length > 0) return notify(`Enter serial numbers for item ${cartItemsWithoutSerialNumbers[0].productId}`, 'error')

        if (lineItems?.length === 0) {
            const { error } = await execute(() => createAndCompleteMintedPhysicalOrder({
                lineItems: cart,
                ...physicalOrder,
                ...formData,
                transactionType: transactionInfo.transactionType,
                globalTransactionNumber: transactionInfo.globalTransactionNumber!
            }))

            if (error === null) {
                notify('Order placed successfully')
                setCapturedSteps(prev => ([...prev, 'PHYSICAL_ORDER']))
                setForceNavigate(true)
            }

        } else {
            const { error } = await execute(() => updateMintedPhysicalOrder({
                lineItems: cart,
                ...formData,
                transactionType: transactionInfo.transactionType,
                globalTransactionNumber: transactionInfo.globalTransactionNumber!
            }))

            if (error === null) {
                notify('Order updated successfully')
                setCapturedSteps(prev => ([...prev, 'PHYSICAL_ORDER']))
                setForceNavigate(true)
            }
        }
    }

    const onVerifyOtpClose = (success?: boolean) => {
        setOpenEmployeeValidationModal(false);
        if(success) {
            setIsEmployeeVerified(true);
            finalSubmit(formData);
        }
    }

    return (
        <>
            {/* {billingData?.payments?.length === 0 && */}
            {transactionInfo.status !== TRANSACTION_STATUS.COMPLETED && //PHYSICAL_ORDER
                <div style={{ padding: '20px 0', display: 'flex', flexDirection: 'column' }}>
                    <div>
                        <OutlinedInput
                            placeholder="Scan QR Code"
                            inputRef={qrRef}
                            size='small'
                            style={{ marginBottom: '5px', width: '30%', float: 'right', fontSize: '0.8rem', padding: '0.2rem 0.1rem' }}
                            onKeyUp={handleKeyUp}
                        />
                    </div>
                    <SearchContainer>
                        <div style={{ width: '100%' }}>
                            <FormControl variant="outlined" fullWidth >
                                <InputLabel htmlFor="search_products">Search Products</InputLabel>
                                <OutlinedInput
                                    id="search_products"
                                    placeholder="By Product Name, Description, Metal type, Purity, Serial Number"
                                    label='Search Products'
                                    endAdornment={
                                        <InputAdornment position='end'>
                                            <Search />
                                        </InputAdornment>
                                    }
                                    value={searchString}
                                    onChange={handleSearch}
                                    disabled={Object.keys(customerConfig).length === 0}
                                />
                            </FormControl>
                        </div>
                    </SearchContainer>
                    <div style={{ display: 'flex', gap: '10px', flexDirection: 'column', paddingTop: '10px' }}>
                        <Table columns={productColumns} rows={showProducts} getRowId={(row: TProductsColumns) => row.skuId} />

                        <SerialNumberSelectorModal
                            availableSerialNumbers={availableSerialNumbers[showSerialNumberModal as string]}
                            onClose={(updatedSerialNumbers) => {
                                setSelectedSerialNumbers(prev => ({ ...prev, [showSerialNumberModal as string]: updatedSerialNumbers }))
                                setCart(prev => {
                                    return [...prev.map(item => {
                                        if (item.productId === showSerialNumberModal) {
                                            return { ...item, serialNumbers: updatedSerialNumbers.join(',') }
                                        } else return item
                                    })]
                                })
                                setShowSerialNumberModal(false)
                            }}
                            quantity={+quantity[showSerialNumberModal as string]}
                            selectedSerialNumbers={selectedSerialNumbers[showSerialNumberModal as string]}
                            show={Boolean(showSerialNumberModal)}
                        />
                        <Title>Add items to Cart</Title>
                        <Table columns={AddedProductColumns} rows={selectedProducts ?? []} getRowId={(row: TProductsColumns) => row.skuId} />
                    </div>
                </div>
            }
            <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
                <MintedPhysicalOrderDetails submitHandler={submitHandler} />
            </div>
            <EmployeeValidationModal 
                open={openEmployeeValidationModal}
                onClose={onVerifyOtpClose}
                dgCustomerRefNo={customerConfig.dgCustomerRefNo!}
                phone={otpSentTo}
            />
        </>
    )
}

const getAddressesOptions = (addresses: ICustomerAddress[] | TPvcCenterAddress[]) => {
    return addresses?.map(address => ({ label: address?.name + ' - ' + address?.line1 + ' - ' + address?.city, value: address.id }))
}

const MintedPhysicalOrderDetails = ({ submitHandler }: { submitHandler: any }) => {
    const { registerForm, setFormData, formData, setField } = useDynamicForm({ formConfig: ORDER_DETAILS_FORM })
    const { customerConfig,setCustomerAddresses, customerAddresses } = useContext(CustomerContext)
    const { transactionInfo } = useContext(TransactionContext);
    const { user: { centerId }, pvcAddresses } = useContext(AuthContext)
    let customerGstAddressesArray  : any = []
    const [customerGstAddresses, setCustomerGstAddresses] = useState<ICustomerAddress[]>([]);
    const [shippingAddressId, setShippingAddressId] = useState<any>();
    const { lineItems, physicalOrder, capturedSteps, transactionInfo: { status } } = useContext(TransactionContext)
    const { execute } = useService()

    useEffect(() => {
     if(!transactionInfo?.globalTransactionNumber){
        setCustomerAddresses([])
        setField('shippingAddressId', { options: []}) 
        setField('billingAddressId', { options: []}) 
     }
    }, [transactionInfo])
    

    const updateForm = () => {
        setField('customerId', { options: [{ label: customerConfig.displayName, value: customerConfig.customerRefNo }] })
    }

    useEffect(() => {
        updateForm()
        if (customerConfig.customerRefNo)
            execute(() => getCustomerActiveAddresses({ customerRefNo: customerConfig.customerRefNo! }))
        if (centerId && customerConfig.isCorporate != 'Y') execute(() => getPvcCenterAddresses({ pvcCenterId: centerId }))

    }, [centerId, customerConfig])

    useEffect(() => {
        if(customerConfig?.gstin){
            const gstStateCode = customerConfig?.gstin.slice(0, 2);
            for (let addresses of customerAddresses) {
                if (addresses.stateCode == gstStateCode ) {
                    customerGstAddressesArray.push(addresses)
                }
              }
              setCustomerGstAddresses(customerGstAddressesArray)
        }
        // setField('billingAddressId', { options: getAddressesOptions(customerAddresses) })
        setField('billingAddressId', { options:  getAddressesOptions(customerAddresses) })
        setField('shippingAddressId', { options: getAddressesOptions(customerConfig.isCorporate === 'Y' ? customerAddresses : pvcAddresses) })

        setFormData({ ...formData, isShippingAsbillingAddressId: false })
        if(customerConfig?.gstin || customerConfig.isCorporate === "N") setFormData({ ...formData, isShippingAsbillingAddressId: true })

    }, [ pvcAddresses])

    useEffect(() => {
        if (formData?.billingAddressId) setField('isShippingAsbillingAddressId', { isVisible: true })
    }, [formData?.billingAddressId])

    useEffect(() => {

        const isShippingAsbillingAddressId = formData.shippingAddressId === formData.billingAddressId

        setFormData({
            ...formData,
            isShippingAsbillingAddressId
        })
    }, [formData?.shippingAddressId, formData.billingAddressId])

    useEffect(() => {
        if (formData.isDoorstepDelivery || customerConfig.isCorporate === 'Y' || customerConfig?.gstin) {
            if (customerConfig.isCorporate != 'Y') {
                if (pvcAddresses.map(address => address.id).includes(formData.shippingAddressId!)) {
                    setFormData({ ...formData, shippingAddressId: '' })
                }
            }
            if(!customerConfig?.gstin && !customerAddresses.map(address => address.id).includes(formData.shippingAddressId!) && customerConfig.isCorporate != 'Y'){
                setShippingAddressId(null)
                setFormData({ ...formData, shippingAddressId: '' })
            }
            setField('shippingAddressId', { isVisible: true, options: customerConfig?.gstin ? getAddressesOptions(customerGstAddresses) : getAddressesOptions(customerAddresses) })
            if(formData.billingAddressId) setField('isShippingAsbillingAddressId', { isVisible: true })
        } else {
            setField('shippingAddressId', { isVisible: true, options:  getAddressesOptions(pvcAddresses) })
            setField('billingAddressId', { isVisible: true, options:  getAddressesOptions(customerAddresses) })
            setField('isShippingAsbillingAddressId', { isVisible: false })
            setFormData({ ...formData, isShippingAsbillingAddressId: false })
            if (customerAddresses.map(address => address.id).includes(formData.shippingAddressId!) && !formData.isDoorstepDelivery) {
                setFormData({ ...formData, shippingAddressId: '' })
            }
        }

    }, [physicalOrder ,formData.isDoorstepDelivery, submitHandler])


    

    useEffect(() => {

        if (formData.isShippingAsbillingAddressId) setFormData({ ...formData, shippingAddressId: formData.billingAddressId })

    }, [formData.isShippingAsbillingAddressId])

    useEffect(() => {
      
        if(formData && formData?.shippingAddressId){
            setShippingAddressId(formData.shippingAddressId)
        }      
      
    }, [formData?.shippingAddressId ])
    

    useEffect(() => {
        if (physicalOrder.isDoorstepDelivery || customerConfig.isCorporate === 'Y' || ( customerConfig?.gstin && customerConfig?.isCorporate === "N" )) {
            setField('shippingAddressId', {
                isVisible: true,
                options: getAddressesOptions(customerGstAddresses)
            })

            setField('billingAddressId', {
                isVisible: true,
                options: getAddressesOptions(customerGstAddresses)
            })
        } else {
            setField('shippingAddressId', {
                isVisible: true,
                options: getAddressesOptions(pvcAddresses)
            })

            setField('billingAddressId', {
                isVisible: true,
                options: getAddressesOptions(customerAddresses)
            })
        }

        setFormData({
            id: physicalOrder.id,
            status: physicalOrder.status,
            billingAddressId: physicalOrder.billingAddressId,
            shippingAddressId: physicalOrder.isDoorstepDelivery || customerConfig.isCorporate === "Y" ? physicalOrder.shippingAddressId : physicalOrder.pvcAddressId,
            isDoorstepDelivery:customerConfig.isCorporate === "N" ? customerConfig.gstin ? false : physicalOrder.isDoorstepDelivery : physicalOrder.isDoorstepDelivery,
            isShippingAsbillingAddressId: physicalOrder.billingAddressId === physicalOrder.shippingAddressId
        } as TCompletePhysicalOrderParams)


    }, [physicalOrder])
    useEffect(() => {
        if(customerConfig?.gstin){
            const gstStateCode = customerConfig?.gstin.slice(0, 2);
            for (let addresses of customerAddresses) {
                if (Number(addresses.stateCode) === Number(gstStateCode) && customerGstAddresses?.length < 1 ) {
                    customerGstAddressesArray.push(addresses)
                }
              }
              setCustomerGstAddresses(customerGstAddressesArray)
        }
        if(customerConfig?.isCorporate === "N" && customerConfig?.gstin ){
            setField('billingAddressId', { options: customerConfig?.gstin ? getAddressesOptions(customerGstAddresses) : getAddressesOptions(customerAddresses) })
            setField('shippingAddressId', { options: customerConfig?.gstin ? getAddressesOptions(customerGstAddresses) : getAddressesOptions(customerAddresses)})
            if(customerAddresses.length > 0){
                for(let address of customerGstAddresses ){
                    if (address?.id === formData.billingAddressId){      
                        setField('shippingAddressId', { options: [{ label: address?.name + ' - ' + address?.line1 + ' - ' + address?.city, value: address.id }]}) 
                    }
                   }
            }
        }else {
            setField('shippingAddressId', { options: physicalOrder?.isDoorstepDelivery ? getAddressesOptions(customerAddresses) : getAddressesOptions(pvcAddresses)})
            setField('billingAddressId', { options: getAddressesOptions(customerAddresses)})
        }


    }, [customerAddresses])
    


    return <>
        <div style={{ padding: '10px 0' }}>
            <Title>Physical Order Details</Title>
            <Divider style={{ marginBottom: '10px' }} />
            <DynamicForm {...registerForm()} onSubmit={submitHandler} submitText={lineItems.length > 0 ? 'Update' : 'Confirm'} />
        </div>
        <div style={{ display: 'flex', gap: '20px' }}>
            <div style={{ width: 'calc(50% - 10px)' }}>
                {formData.billingAddressId && customerAddresses.length > 0 ? <AddressCard address={customerAddresses.filter(address => address.id === formData.billingAddressId)[0]} heading='Billing Address' /> : <></>}
            </div>
            <div style={{ width: 'calc(50% - 10px)' }}>
                {formData.isDoorstepDelivery || customerConfig.isCorporate === 'Y'  || customerConfig?.gstin ?
                    (shippingAddressId  && customerAddresses.length > 0 ? <AddressCard address={customerAddresses.filter(address => address.id === shippingAddressId)[0]} heading='Shipping Address' /> : <></>)
                    :
                    // (formData.shippingAddressId && pvcAddresses.length > 0 ? <AddressCard address={pvcAddresses.filter(address => address.id === formData?.shippingAddressId)[0]} heading='Shipping Address' /> : <></> )
                    (formData.shippingAddressId && pvcAddresses.length > 0 && (pvcAddresses?.some(address => address.id === formData.shippingAddressId))
  ? <AddressCard address={pvcAddresses.filter(address => address.id === formData.shippingAddressId)[0]} heading='Shipping Address' />
  : (pvcAddresses.length > 0
    ? <AddressCard address={pvcAddresses[0]} heading='Shipping Address' />
    : <></>)
)
                }
            </div>
        </div>
        {status !== TRANSACTION_STATUS.PHYSICAL_ORDER_INITIATED &&
            <div style={{ padding: '20px 0' }}>
                <Title>Cart Items</Title>
                <Divider style={{ marginBottom: '10px' }} />
                <Table columns={LINE_ITEM_COLUMNS} rows={lineItems} />
            </div>
        }
    </>
}

const CustomerDetail = () => {
    const { customerConfig, customerAddresses } = useContext(CustomerContext)
    const [showCustomerModal, setShowCustomerModal] = useState(false)
    const [showCustomerUpdateModal, setShowCustomerUpdateModal] = useState(false)
    const { id } = useParams()

    const handleCustomer = () => {
        setShowCustomerModal(prev => !prev)
    }
    const closeCustomerSelection = () => {
        setShowCustomerModal(prev => !prev)
    }
    const handleCustomerUpdate = () => {
        setShowCustomerUpdateModal(prev => !prev)
    }
    const closeCustomerUpdateModal = () => {
        setShowCustomerUpdateModal(prev => !prev)
    }
    return (
        <Container>
            <div style={{ borderBottom: '1px solid #f1f1f1', color: '#7f7d76', paddingTop: '1.5%', paddingBottom: '1.5%' }}>
                <Button variant={'contained'} disabled={id && customerConfig.isCorporate === 'Y' ? true : false} onClick={id ? handleCustomerUpdate : handleCustomer}>{id ? 'Update Customer Details' : 'Customer Selection'}</Button>
            </div>
            <Modal onClose={closeCustomerSelection} open={showCustomerModal} title="Customer Selection" >
                <SelectCustomer path={PATHS.MINTED_SALES} nextSection='PHYSICAL_ORDER' onClose={closeCustomerSelection} />
            </Modal>
            <Modal onClose={closeCustomerUpdateModal} open={showCustomerUpdateModal} title="Update Customer Details" >
                <UpdateCustomerDetails onClose={closeCustomerUpdateModal} />
            </Modal>
        </Container>
    )
}

function UpdateCustomerDetails(props: { onClose: () => any }) {
    const { onClose } = props
    const { customerConfig, customerAddresses, mobileVerificationId } = useContext(CustomerContext)
    const { notify } = useContext(NotificationContext)
    const { execute, loading } = useService()
    const { registerForm, setFormData, setFormConfig, formData, setField } = useDynamicForm({
        formConfig: USER_CONFIG.map(field => ({ ...field, disabled: true }))
    })


    const updateCustomers = (data: any) => {
        return execute(() => updateCustomer(data))
    }
    const updateAndSubmitHandler = async (data: TCustomerFields) => {

        let stateCodeFromMaste = await getStateCodeFromMaster(data.state)
        const updatedCustomerData = {
            customer: {
                addresses: [{
                    id: customerAddresses[0]?.id,
                    name: data.fullName,
                    mobileNumber: customerConfig.mobileNumber,
                    line1: data.line1,
                    line2: data.line2,
                    city: data.city,
                    state: data.state,
                    stateCode: stateCodeFromMaste,
                    country: data.country,
                    zip: data.zip,
                    isActive: true,
                    type: 'B',
                    customerRefNo: customerConfig.customerRefNo
                }],
                displayName: data.fullName,
                fullName: data.fullName,
                mainAccountNo: data.mainAccountNo,
                gender: data.gender,
                gstin: data.gstin,
                maritalStatus: '',
                nationality: data.nationality,
                emailAddress: data.emailAddress,
                sendPromotions: customerConfig.sendPromotions,
                isCorporate: customerConfig.isCorporate,
                mobileVerificationId,
                userType : data.userType,
                employeeCode: data.employeeCode,
            },
            customerRefNo: data.customerRefNo!,
            updateReqFromTransaction: true
        }




        const result = await updateCustomers(updatedCustomerData)
        if (result.error == null) {
            await execute(() => getCustomer({ customerRefNo: data.customerRefNo! }))
        } else return notify('internal server error', 'error')

        onClose()
        notify('User details saved!')


    }

    useEffect(() => {
        if (customerAddresses[0].customerRefNo) execute(() => getCustomer({ customerRefNo: customerAddresses[0].customerRefNo! }))
    }, [])

    useEffect(() => {
        getCityFromMaster(customerAddresses[0].state)
        setFormData({ ...customerAddresses[0], ...customerConfig } as any)

    }, [ customerAddresses,])

    useEffect(() => {
        getCityFromMaster(formData.state)


    }, [formData.state])



    async function getCityFromMaster(stateorcity: any) {
        let stateCode = false
        const { data, error } = await execute(() => getCityStateFromMts({ stateorcity, stateCode }))

        if (data!) {
            let updated_cities = [];
            //@ts-ignore
            for (let i = 0; i < data.length; i++) {
                //@ts-ignore
                let cities = { value: data[i].domain_description, label: data[i].domain_description, code: "25" }
                updated_cities.push(cities)

            }
            setField('city', { options: updated_cities })

        }

    }

    let stateorcity = "CNIND"
    async function getstatecity() {
        let stateCode = false


        const { data, error } = await execute(() => getCityStateFromMts({ stateorcity, stateCode }))
        if (data! && MTSStates.length === 0) {
            //@ts-ignore
            for (let i = 0; i < data.length; i++) {
                //@ts-ignore
                let state = { value: data[i].domain_description, label: data[i].domain_description, code: data[i].state_code }
                MTSStates.push(state);
            }
        }


    }

    async function getStateCodeFromMaster(stateorcity: any) {
        let stateCode = true;
        const { data, error } = await execute(() => getCityStateFromMts({ stateorcity, stateCode }))
        //@ts-ignore
        if (data != null) return data[0]?.state_code
    }

    useEffect(() => {
        let showEmployee= formData.userType === 'employee' || formData.userType === 'F&F'
        setField('employeeCode', {isVisible:showEmployee , validation:{ required:showEmployee }})
    }, [formData.userType])

    useEffect(() => {
        getstatecity();
        setFormConfig(prev => {
            return [...prev.map(field => {
                let conditionToDisable = field.fieldName === 'country' ||
                    field.fieldName === "city" ||
                    field.fieldName === "state" ||
                    field.fieldName === "line1" ||
                    field.fieldName === "line2" ||
                    field.fieldName === "zip"
                if (field.fieldName === 'mobileNumber') {
                    field.disabled = true
                } else if (conditionToDisable && customerConfig.isCorporate === 'Y') {
                    return { ...field, isVisible: false, validation: { required: false } }
                } else field.disabled = false
                if(field.fieldName === 'fullName')
                    field.disabled = true

                return field
            })]
        })
    }, [])

    return (
        <div style={{ padding: '20px 10px' }}>
            {!loading ? <DynamicForm {...registerForm()} onSubmit={updateAndSubmitHandler} submitText={'Save and Continue'} onCancel={onClose} cancelText='Back' /> : <></>}
        </div>
    )
}

export default MintedSales